import { Button, Icon } from '@britishcouncil/react-admin';
import styled from '@emotion/styled';
import { SpeakingSlotType } from 'api/planning';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { RadioButton, SpeakingTypeBadge } from 'components';
import { ROUTING_PATHS } from 'const';
import { useFeatureFlags, useIntParam } from 'hooks';
import { generatePath, Link } from 'react-router';
import { Dropdown, DropdownItem, DropdownMenu } from 'semantic-ui-react';

type Props = {
  onAddRow: (speakingSlotType: SpeakingSlotType) => void;
  onSave: () => void;
  selectedSlotAction: SpeakingSlotAction;
  setSelectedSlotAction: (value: SpeakingSlotAction) => void;
  availableSpeakingSlotTypes: SpeakingSlotType[];
  saving: boolean;
};

export const EditRoomBar = ({
  onAddRow,
  onSave,
  selectedSlotAction,
  setSelectedSlotAction,
  availableSpeakingSlotTypes,
  saving,
}: Props) => {
  const { speakingSessionDetails } = ROUTING_PATHS;
  const sessionId = useIntParam('sessionId');
  const { isComputerMediatedSpeakingEnabled } = useFeatureFlags();

  const onAddSpeakingRoomStructure = (
    _e: React.MouseEvent,
    { value }: { value?: string | number | boolean }
  ) => {
    onAddRow(value as SpeakingSlotType);
  };

  return (
    <S.Subgrid>
      <S.RowLeft className="left-panel">
        <S.Dropdown text="Add row" icon="plus" direction="right" data-testid="add-row">
          <S.DropdownMenu>
            <S.DropdownItem
              disabled={!availableSpeakingSlotTypes.includes(SpeakingSlotType.IPC)}
              className="grey"
              icon="plus"
              value={SpeakingSlotType.IPC}
              content={
                <S.DropdownOptionContent>
                  <S.BoldText>In-Person Classic</S.BoldText>
                  <div>
                    <SpeakingTypeBadge speakingSlotType={SpeakingSlotType.IPC} />
                  </div>
                </S.DropdownOptionContent>
              }
              onClick={onAddSpeakingRoomStructure}
              data-testid="add-row-ipc"
            />
            <S.DropdownItem
              disabled={!availableSpeakingSlotTypes.includes(SpeakingSlotType.IPA)}
              className="teal"
              icon="plus"
              value={SpeakingSlotType.IPA}
              content={
                <S.DropdownOptionContent>
                  <S.BoldText>In-Person Adapt</S.BoldText>
                  <div>
                    <SpeakingTypeBadge speakingSlotType={SpeakingSlotType.IPA} />
                  </div>
                </S.DropdownOptionContent>
              }
              onClick={onAddSpeakingRoomStructure}
              data-testid="add-row-ipa"
            />
            <S.DropdownItem
              disabled={!availableSpeakingSlotTypes.includes(SpeakingSlotType.RSS)}
              className="pink"
              icon="plus"
              value={SpeakingSlotType.RSS}
              content={
                <S.DropdownOptionContent>
                  <S.BoldText>Remote Speaking</S.BoldText>
                  <div>
                    <SpeakingTypeBadge speakingSlotType={SpeakingSlotType.RSS} />
                  </div>
                </S.DropdownOptionContent>
              }
              onClick={onAddSpeakingRoomStructure}
              data-testid="add-row-rss"
            />
            <S.DropdownItem
              disabled={
                !isComputerMediatedSpeakingEnabled ||
                !availableSpeakingSlotTypes.includes(SpeakingSlotType.SWA)
              }
              className="violet"
              icon="plus"
              value={SpeakingSlotType.SWA}
              content={
                <S.DropdownOptionContent>
                  <S.BoldText>Avatar Speaking</S.BoldText>
                  <div>
                    <SpeakingTypeBadge speakingSlotType={SpeakingSlotType.SWA} />
                  </div>
                </S.DropdownOptionContent>
              }
              onClick={onAddSpeakingRoomStructure}
              data-testid="add-row-swa"
            />
          </S.DropdownMenu>
        </S.Dropdown>
      </S.RowLeft>
      <S.RowRight>
        <S.StructureControls>
          <S.PillBackground>
            <RadioButton
              onSelect={setSelectedSlotAction}
              value={SpeakingSlotAction.AddSlot}
              selected={selectedSlotAction}
              data-testid="add-slot"
            >
              <Icon name="plus square" />
              Add slot
            </RadioButton>
            <RadioButton
              onSelect={setSelectedSlotAction}
              value={SpeakingSlotAction.AddBreak}
              selected={selectedSlotAction}
              data-testid="add-break"
            >
              <Icon name="pause" />
              Add break
            </RadioButton>
            <RadioButton
              onSelect={setSelectedSlotAction}
              value={SpeakingSlotAction.Clear}
              selected={selectedSlotAction}
              data-testid="clear"
            >
              <Icon name="times circle" />
              Clear
            </RadioButton>
          </S.PillBackground>
        </S.StructureControls>
        <S.RoomControls>
          <Button
            secondary
            compact
            size="small"
            as={Link}
            to={generatePath(speakingSessionDetails.absolutePath, {
              sessionId,
            })}
            data-testid="discard"
          >
            Discard
          </Button>
          <Button
            color="green"
            compact
            size="small"
            disabled={saving}
            loading={saving}
            onClick={onSave}
            data-testid="save"
          >
            Save
          </Button>
        </S.RoomControls>
      </S.RowRight>
    </S.Subgrid>
  );
};

const S = {
  BoldText: styled.div`
    font-weight: bold;
  `,
  DropdownOptionContent: styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
  `,
  DropdownItem: styled(DropdownItem)`
    .ui.dropdown > .menu > &.item {
      display: flex;
      align-items: center;
      &.grey {
        color: #415561;
      }
      &.pink {
        color: #de3494;
      }
      &.teal {
        color: #19b6ad;
      }
      &.violet {
        color: #9f07d2;
      }
    }
  `,
  DropdownMenu: styled(DropdownMenu)`
    &.menu {
      left: 0 !important;
      right: auto !important;
      top: calc(100% + 4px) !important;
      bottom: auto !important;
      transform: none !important;
      z-index: 1000;
    }

    &.menu.visible,
    &.menu.visible.transition {
      display: grid !important;
      grid-template-columns: 1fr 1fr;
    }
  `,
  Dropdown: styled(Dropdown)`
    &.ui.dropdown {
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
      position: relative;

      & > .icon {
        font-size: 14px;
      }
    }
  `,
  Subgrid: styled.div`
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  `,
  RowLeft: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fafafb;
    color: #2185d0;
    font-size: 12px;
    font-weight: bold;
    border-left: 1px solid #e2e2e3;
    border-bottom: 1px solid #e2e2e3;
    border-bottom-left-radius: 5px;
  `,
  RowRight: styled.div`
    display: grid;
    grid-column: 2 / 4;
    grid-template-columns: 1fr auto;
    box-shadow: inset 10px 0 10px -10px #d5d5d5;
  `,
  StructureControls: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  RoomControls: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    & > .ui.button {
      margin: 0;
    }
  `,
  PillBackground: styled.div`
    background-color: #f3f4f5;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    border-radius: 25px;
    margin-top: 8px;
  `,
};
